import { useEffect, useState } from "react";
import axios from "axios";
import { Filter, Workflow, AlertTriangle, RefreshCw, ChevronDown, ChevronRight, MapPin, Trophy, Clock } from "lucide-react";
import { API } from "../../config";

// ─── Helpers ──────────────────────────────────────────────────────────

const fmtHours = (h) => {
  if (h === null || h === undefined || h === 0) return "—";
  if (h < 1) return `${Math.round(h * 60)} min`;
  if (h < 24) return `${h.toFixed(1)} h`;
  const d = h / 24;
  return d < 7 ? `${d.toFixed(1)} g` : `${(d / 7).toFixed(1)} sett.`;
};

// Color a percentage bar dynamically (higher = better).
const barColor = (pct) => {
  if (pct >= 75) return "bg-lojack-blue-500";
  if (pct >= 40) return "bg-lojack-clause-500";
  if (pct >= 10) return "bg-orange-500";
  return "bg-red-500";
};

const dropColor = (pct) => {
  if (pct >= 50) return "text-red-400";
  if (pct >= 25) return "text-lojack-clause-400";
  return "text-gray-400";
};

// Pretty step icons (kept generic to avoid emoji overload).
const STEP_DOTS = ["①", "②", "③", "④", "⑤"];


export const FunnelAnalyticsWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [showStuck, setShowStuck] = useState(false);

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [country, setCountry] = useState("");
  const [customerType, setCustomerType] = useState("");
  const [staleDays, setStaleDays] = useState(3);

  const buildQs = () => {
    const params = new URLSearchParams();
    if (dateFrom) params.set("date_from", dateFrom);
    if (dateTo) params.set("date_to", dateTo);
    if (country) params.set("country", country);
    if (customerType) params.set("customer_type", customerType);
    if (staleDays) params.set("stale_days", staleDays);
    return params.toString();
  };

  const fetchFunnel = async () => {
    try {
      setLoading(true);
      const qs = buildQs();
      const r = await axios.get(`${API}/dashboard/funnel${qs ? "?" + qs : ""}`);
      setData(r.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("funnel fetch failed", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunnel();
    // Auto-refresh every 2 minutes (lighter than 60s — payload is bigger)
    const t = setInterval(fetchFunnel, 120_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setDateFrom(""); setDateTo(""); setCountry(""); setCustomerType(""); setStaleDays(3);
    // Re-fetch on next tick (state updates async)
    setTimeout(fetchFunnel, 0);
  };

  if (!data) {
    return (
      <div className="bg-[#111] border border-gray-800 rounded-2xl p-6" data-testid="funnel-widget-empty">
        {loading
          ? <div className="text-gray-500 text-sm flex items-center gap-2"><RefreshCw size={14} className="animate-spin" /> Caricamento funnel…</div>
          : <p className="text-gray-500 text-sm">Nessun dato funnel disponibile</p>}
      </div>
    );
  }

  const steps = data.steps || [];
  const topLeaderboard = (data.dealer_leaderboard || []).filter(d => d.dealer_id).slice(0, 5);
  const countries = (data.country_breakdown || []).slice(0, 8);

  return (
    <div
      className="bg-gradient-to-br from-[#1a0f1f] via-[#0a0a0f] to-[#1a0f1f] border border-purple-500/20 rounded-2xl p-6 space-y-5"
      data-testid="funnel-analytics-widget"
    >
      {/* ─── Header ─────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
            <Workflow size={20} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Customer Onboarding Funnel</h3>
            <p className="text-gray-500 text-xs">
              {data.total_customers} clienti totali · conversione globale{" "}
              <span className="text-purple-400 font-semibold">{data.overall_conversion_pct.toFixed(1)}%</span>
              {data.stuck_count > 0 && (
                <> · <span className="text-lojack-clause-400">{data.stuck_count}</span> bloccati &gt; {data.filters_applied.stale_days}g</>
              )}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowFilters(s => !s)}
            className={`px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 transition-colors ${
              showFilters ? "bg-purple-500/20 text-purple-300" : "bg-gray-800 text-gray-400 hover:text-purple-400"
            }`}
            data-testid="funnel-toggle-filters"
          >
            <Filter size={12} /> Filtri
          </button>
          <button
            onClick={fetchFunnel}
            disabled={loading}
            className="p-2 text-gray-500 hover:text-purple-400 transition-colors"
            title="Aggiorna"
            data-testid="funnel-refresh"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* ─── Filters panel ──────────────────────────────────────────── */}
      {showFilters && (
        <div className="bg-black/40 border border-gray-800 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="funnel-filters">
          <div>
            <label className="text-gray-500 text-[10px] uppercase tracking-wider">Da</label>
            <input
              type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="w-full mt-1 bg-[#0a0a0a] border border-gray-800 rounded-md px-2 py-1.5 text-white text-xs"
              data-testid="funnel-filter-date-from"
            />
          </div>
          <div>
            <label className="text-gray-500 text-[10px] uppercase tracking-wider">A</label>
            <input
              type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="w-full mt-1 bg-[#0a0a0a] border border-gray-800 rounded-md px-2 py-1.5 text-white text-xs"
              data-testid="funnel-filter-date-to"
            />
          </div>
          <div>
            <label className="text-gray-500 text-[10px] uppercase tracking-wider">Paese</label>
            <select
              value={country} onChange={e => setCountry(e.target.value)}
              className="w-full mt-1 bg-[#0a0a0a] border border-gray-800 rounded-md px-2 py-1.5 text-white text-xs"
              data-testid="funnel-filter-country"
            >
              <option value="">Tutti</option>
              {(data.country_breakdown || []).map(c => (
                <option key={c.country} value={c.country}>{c.country}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-[10px] uppercase tracking-wider">Tipo</label>
            <select
              value={customerType} onChange={e => setCustomerType(e.target.value)}
              className="w-full mt-1 bg-[#0a0a0a] border border-gray-800 rounded-md px-2 py-1.5 text-white text-xs"
              data-testid="funnel-filter-type"
            >
              <option value="">Tutti</option>
              <option value="private">Privato</option>
              <option value="business">Business</option>
            </select>
          </div>
          <div>
            <label className="text-gray-500 text-[10px] uppercase tracking-wider">Stale (gg)</label>
            <input
              type="number" min="1" max="90" value={staleDays}
              onChange={e => setStaleDays(parseInt(e.target.value) || 3)}
              className="w-full mt-1 bg-[#0a0a0a] border border-gray-800 rounded-md px-2 py-1.5 text-white text-xs"
              data-testid="funnel-filter-stale-days"
            />
          </div>
          <div className="col-span-2 md:col-span-5 flex justify-end gap-2 mt-1">
            <button
              onClick={resetFilters}
              className="px-3 py-1.5 text-xs text-gray-400 hover:text-white transition-colors"
              data-testid="funnel-filter-reset"
            >
              Reset
            </button>
            <button
              onClick={fetchFunnel}
              className="px-4 py-1.5 bg-purple-500 hover:bg-purple-600 text-white text-xs rounded-md font-medium transition-colors"
              data-testid="funnel-filter-apply"
            >
              Applica
            </button>
          </div>
        </div>
      )}

      {/* ─── Funnel visualization ──────────────────────────────────── */}
      <div className="space-y-2" data-testid="funnel-steps">
        {steps.map((s, i) => {
          const widthPct = Math.max(5, s.percentage); // min 5% so the bar is visible even when empty
          return (
            <div key={s.key} className="bg-black/40 border border-gray-800 rounded-xl p-3.5" data-testid={`funnel-step-${s.key}`}>
              <div className="flex items-center gap-3">
                <span className="text-purple-400 text-lg font-bold w-6">{STEP_DOTS[i] || (i + 1)}</span>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-white text-sm font-medium truncate">{s.label}</span>
                      {i > 0 && s.drop_off_from_prev_pct > 0 && (
                        <span className={`text-[10px] ${dropColor(s.drop_off_from_prev_pct)}`}>
                          −{s.drop_off_from_prev_pct.toFixed(1)}% drop
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs whitespace-nowrap">
                      <span className="text-white font-bold" data-testid={`funnel-count-${s.key}`}>{s.count}</span>
                      <span className="text-gray-500">({s.percentage.toFixed(1)}%)</span>
                      {i > 0 && s.sample_size_time > 0 && (
                        <span className="text-gray-600 flex items-center gap-1">
                          <Clock size={10} /> {fmtHours(s.avg_time_from_prev_h)}
                        </span>
                      )}
                    </div>
                  </div>
                  {/* Funnel bar */}
                  <div className="h-2 bg-gray-900 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${barColor(s.percentage)} transition-all duration-500 rounded-full`}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Stuck customers (collapsible) ─────────────────────────── */}
      {data.stuck_count > 0 && (
        <div className="bg-black/40 border border-lojack-clause-900/40 rounded-xl p-4" data-testid="funnel-stuck-block">
          <button
            onClick={() => setShowStuck(s => !s)}
            className="w-full flex items-center justify-between text-left"
            data-testid="funnel-toggle-stuck"
          >
            <div className="flex items-center gap-2">
              {showStuck ? <ChevronDown size={14} className="text-lojack-clause-400" /> : <ChevronRight size={14} className="text-lojack-clause-400" />}
              <AlertTriangle size={14} className="text-lojack-clause-400" />
              <span className="text-white text-sm font-semibold">
                Clienti bloccati nel funnel
              </span>
              <span className="text-lojack-clause-400 text-xs">({data.stuck_count})</span>
            </div>
            <span className="text-gray-500 text-[10px]">&gt; {data.filters_applied.stale_days} giorni</span>
          </button>

          {showStuck && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-800 text-gray-500 text-[10px] uppercase tracking-wider">
                    <th className="text-left py-2 font-medium">Cliente</th>
                    <th className="text-left py-2 font-medium">Paese</th>
                    <th className="text-left py-2 font-medium">Bloccato a</th>
                    <th className="text-right py-2 font-medium">Giorni</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stuck_customers.map((c, i) => (
                    <tr key={c.id || i} className="border-b border-gray-800/50" data-testid={`funnel-stuck-row-${i}`}>
                      <td className="py-2 text-white truncate max-w-[180px]" title={c.email}>{c.name}</td>
                      <td className="py-2 text-gray-400">{c.country || "—"}</td>
                      <td className="py-2 text-gray-400">{c.stuck_at_label}</td>
                      <td className="py-2 text-right">
                        <span className={c.days_stuck >= 14 ? "text-red-400 font-semibold" : "text-lojack-clause-400"}>
                          {c.days_stuck}g
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.stuck_count > data.stuck_customers.length && (
                <p className="text-gray-600 text-[10px] mt-2 text-center">
                  Mostrati {data.stuck_customers.length} di {data.stuck_count} bloccati. Affina i filtri per restringere.
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Country & dealer side panels ──────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Per-country */}
        {countries.length > 0 && (
          <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="funnel-country-breakdown">
            <div className="flex items-center gap-2 mb-3">
              <MapPin size={14} className="text-blue-400" />
              <p className="text-white text-sm font-semibold">Conversione per Paese</p>
            </div>
            <div className="space-y-1.5">
              {countries.map(c => (
                <div key={c.country} className="flex items-center gap-3 text-xs" data-testid={`funnel-country-${c.country}`}>
                  <span className="text-white font-mono w-8">{c.country}</span>
                  <div className="flex-1 h-1.5 bg-gray-900 rounded-full overflow-hidden">
                    <div className={`h-full ${barColor(c.conversion_pct)}`} style={{ width: `${Math.max(2, c.conversion_pct)}%` }} />
                  </div>
                  <span className="text-gray-400 w-20 text-right">
                    {c.signed}/{c.total} · {c.conversion_pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dealer leaderboard */}
        {topLeaderboard.length > 0 && (
          <div className="bg-black/40 border border-gray-800 rounded-xl p-4" data-testid="funnel-dealer-leaderboard">
            <div className="flex items-center gap-2 mb-3">
              <Trophy size={14} className="text-lojack-clause-400" />
              <p className="text-white text-sm font-semibold">Top Dealer (Funnel)</p>
            </div>
            <div className="space-y-1.5">
              {topLeaderboard.map((d, i) => (
                <div key={d.dealer_id} className="flex items-center gap-3 text-xs" data-testid={`funnel-dealer-${i}`}>
                  <span className="text-gray-500 w-5">{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}</span>
                  <span className="text-white truncate flex-1" title={d.dealer_email || d.dealer_name}>{d.dealer_name}</span>
                  <span className="text-gray-400 whitespace-nowrap">
                    {d.signed}/{d.total} · {d.conversion_pct.toFixed(1)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <p className="text-gray-600 text-[10px] text-center">
        Ultimo aggiornamento: {new Date(data.generated_at).toLocaleString("it-IT")} · Auto-refresh ogni 120s
      </p>
    </div>
  );
};

export default FunnelAnalyticsWidget;
